import { ActivityEntityName, EntityViewDisplay, PublicHoliday, SurfaceType, manageTenancyRights } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import type { EntityManifestCard } from '@workbuddy/services/entities/entity.manifest';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { publicHolidayCloneYearCommands } from './commands/clone.year.command';
import { handlePublicHolidayBulkCommand, publicHolidayBulkCommands } from './commands/holiday.bulk.commands';

const card: EntityManifestCard<PublicHoliday> = {
  component: () => import('./presentation/card.public.holiday').then(module => module.CardPublicHolidayComponent),
  resolution: 'snapshot',
  summarize: holiday => ({ title: holiday.name ?? 'Public Holiday', line1: holiday.origin ?? holiday.kind, line2: holiday.year ? String(holiday.year) : undefined }),
};

export const publicHolidayManifest = registerManifest<PublicHoliday>({
  entity: ActivityEntityName.PublicHoliday,
  slug: 'public-holiday',
  routes: {
    root: '/settings/public-holiday',
  },
  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    defaults: {
      shapeFilterFields: fields => fields.filter(field => field.name === 'origin'),
      header: {
        title: 'Public Holidays',
        showSearch: true,
        showPaginator: true,
        showViewOptions: false,
        primaryCommands: publicHolidayCloneYearCommands,
      },
      itemInteraction: {
        selectable: {
          type: 'multiple',
          bulkCommands: publicHolidayBulkCommands,
        },
      },
      table: {
        columns: {
          byField: {
            states: { minWidth: 320, flex: 1, wrapText: true, autoHeight: true },
          },
        },
      },
      bulk: {
        onCommand: handlePublicHolidayBulkCommand,
      },
    },
  },
  presentation: { card },
  surfaces: {
    [SurfaceType.Form]: () => import('./surface/form/public.holiday.form.surface').then(module => module.publicHolidayFormSurface),
  },
  operations: {
    create: {
      surface: SurfaceType.Form,
      presentation: { mode: 'dialog', title: 'New Public Holiday', width: 'min(560px, calc(100vw - 40px))' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(...manageTenancyRights),
    },
    edit: {
      surface: SurfaceType.Form,
      presentation: { mode: 'dialog', title: 'Public Holiday', width: 'min(560px, calc(100vw - 40px))' },
      authorize: ({ injector }) => injector.get(RightsService).hasRights(...manageTenancyRights),
    },
  },
});
